import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Spin, Select, Button, Tooltip, Tabs } from 'antd';
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import TimeRangeFilter, { TimeRange } from '../../../components/dashboard/TimeRangeFilterPickup';
import { ScaleQueryType } from '../../../graphql/types/dashboard/ScaleDashboardTypes';
import { DASHBOARD_PACIFIC_TZ } from '../../../utils/dashboardPacificTime';
import {
    DASHBOARD_EXCLUDE_HUB_LABELS,
    useOperableHubSelectOptions,
} from '../../../hooks/useOperableHubSelectOptions';
import { QUERY_PICKUP_DASHBOARD } from '../../../graphql/queries/dashboard/queryPickupDashboard';
import { QUERY_PICKUP_CUSTOMER_OPTIONS } from '../../../graphql/queries/dashboard/queryPickupCustomerOptions';
import {
    PickupDashboardSummaryItem,
    QueryPickupDashboardResponse,
    QueryPickupCustomerOptionsResponse,
} from '../../../graphql/types/dashboard/PickupDashboardTypes';
import {
    PICKUP_DASHBOARD_METRICS,
    MetricCategory,
    MetricRow,
    DrillType,
} from './pickupDashboardMetrics';
import PickupReservationDrawer from './PickupReservationDrawer';
import PickupSiteDrawer from './PickupSiteDrawer';

dayjs.extend(isoWeek);

// ----------------------------- helpers -----------------------------

const WEEKDAY_ZH = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

function formatPeriodHeader(pk: string, queryType: ScaleQueryType | undefined): string {
    if (queryType !== ScaleQueryType.DAILY) return pk;
    const day = dayjs.tz(pk, DASHBOARD_PACIFIC_TZ).day();
    return `${pk} ${WEEKDAY_ZH[day]}`;
}

function getPercentColor(value: number): string | undefined {
    if (value >= 95) return '#52c41a';
    if (value >= 85) return '#faad14';
    return '#ff4d4f';
}

function computePeriodKey(bizDate: string, queryType: ScaleQueryType): string {
    if (queryType === ScaleQueryType.DAILY) {
        return bizDate;
    }
    if (queryType === ScaleQueryType.WEEKLY) {
        const d = dayjs(bizDate);
        const weekStart = d.startOf('isoWeek').format('YYYY-MM-DD');
        const weekEnd = d.endOf('isoWeek').format('YYYY-MM-DD');
        return `${weekStart}~${weekEnd}`;
    }
    // MONTHLY
    const d = dayjs(bizDate);
    return d.format('YYYY-MM');
}

function sumFields(
    items: PickupDashboardSummaryItem[],
    fields: (keyof PickupDashboardSummaryItem)[]
): number {
    return fields.reduce((acc, f) => {
        return acc + items.reduce((s, item) => s + Number(item[f] ?? 0), 0);
    }, 0);
}

function computeMetricValue(
    items: PickupDashboardSummaryItem[],
    metric: MetricRow
): { display: string; numericPct: number | null } {
    if (items.length === 0) return { display: '—', numericPct: null };

    if (metric.isRate) {
        const num = sumFields(items, metric.numeratorFields);
        const den = sumFields(items, metric.denominatorFields);
        if (den === 0) return { display: '—', numericPct: null };
        const ratio = num / den;
        const pct = (metric.invertRate ? 1 - ratio : ratio) * 100;
        const rounded = Math.round(pct * 10) / 10;
        return { display: `${rounded.toFixed(1)}%`, numericPct: rounded };
    }

    if (metric.unit === 'h') {
        // package_online_duration 存储精度为 DECIMAL(12,1)，先四舍五入到 1 位小数消除 JS 浮点累加误差，再除
        const num = Math.round(sumFields(items, metric.numeratorFields) * 10) / 10;
        const den = sumFields(items, metric.denominatorFields);
        if (den === 0) return { display: '—', numericPct: null };
        const avg = num / den;
        const rounded = Math.round(avg * 100) / 100;
        return { display: `${rounded.toFixed(2)}h`, numericPct: null };
    }

    // plain count
    const total = sumFields(items, metric.numeratorFields);
    return { display: String(total), numericPct: null };
}

// 收集某组 allowedCategoryKeys 下所有可展开行的 key
function getAllExpandableKeys(allowedCategoryKeys: string[]): string[] {
    const keys: string[] = [];
    const collect = (rows: MetricRow[]) => {
        rows.forEach((row) => {
            if (row.children && row.children.length > 0) {
                keys.push(row.key);
                collect(row.children);
            }
        });
    };
    PICKUP_DASHBOARD_METRICS
        .filter((cat) => allowedCategoryKeys.includes(cat.key))
        .forEach((cat) => collect(cat.children));
    return keys;
}

// ----------------------------- types -----------------------------

interface DrillState {
    visible: boolean;
    type: DrillType;
    bizDate: string | undefined;
    drillFilter?: Record<string, number>;
}

// ----------------------------- tab content component -----------------------------

interface PickupTabContentProps {
    allowedCategoryKeys: string[];
    items: PickupDashboardSummaryItem[];
    itemsByPeriod: Map<string, PickupDashboardSummaryItem[]>;
    periodKeys: string[];
    loading: boolean;
    timeRange: TimeRange | null;
    hubIdsForQuery: number[] | undefined;
    customerIdsForQuery: number[] | undefined;
    ewrType: string | undefined;
    expandedKeys: Set<string>;
    onToggleExpand: (key: string) => void;
}

const PickupTabContent: React.FC<PickupTabContentProps> = ({
    allowedCategoryKeys,
    items,
    itemsByPeriod,
    periodKeys,
    loading,
    timeRange,
    hubIdsForQuery,
    customerIdsForQuery,
    ewrType,
    expandedKeys,
    onToggleExpand,
}) => {
    const { t } = useTranslation();

    const [drillState, setDrillState] = useState<DrillState>({
        visible: false,
        type: 'none',
        bizDate: undefined,
        drillFilter: undefined,
    });

    const visibleCategories = useMemo(
        () => PICKUP_DASHBOARD_METRICS.filter((cat) => allowedCategoryKeys.includes(cat.key)),
        [allowedCategoryKeys]
    );

    const handleCellClick = useCallback((drillType: DrillType, bizDate: string | undefined, drillFilter?: Record<string, number>) => {
        if (drillType === 'none') return;
        setDrillState({ visible: true, type: drillType, bizDate, drillFilter });
    }, []);

    const handleDrawerClose = useCallback(() => {
        setDrillState((prev) => ({ ...prev, visible: false }));
    }, []);

    // Render a metric value cell
    const renderMetricCell = (
        metric: MetricRow,
        periodKey: string | null,
        isCategoryRow: boolean
    ): React.ReactNode => {
        if (isCategoryRow) return <span style={{ color: '#888' }}>—</span>;

        const cellItems = periodKey != null ? (itemsByPeriod.get(periodKey) ?? []) : items;
        const { display, numericPct } = computeMetricValue(cellItems, metric);

        if (display === '—') return <span style={{ color: '#888' }}>—</span>;

        const drillable = metric.drillType !== 'none' && periodKey !== null;
        const color = metric.isRate && numericPct !== null ? getPercentColor(numericPct) : undefined;

        return (
            <span
                style={{
                    color: color ?? 'inherit',
                    cursor: drillable ? 'pointer' : 'default',
                    fontWeight: color ? 600 : undefined,
                    textDecoration: drillable ? 'underline' : undefined,
                }}
                onClick={drillable ? () => handleCellClick(metric.drillType, periodKey, metric.drillFilter) : undefined}
            >
                {display}
            </span>
        );
    };

    const renderRows = (
        metrics: MetricRow[],
        depth: number,
        categoryDrillType: DrillType
    ): React.ReactNode[] => {
        const rows: React.ReactNode[] = [];

        metrics.forEach((metric) => {
            const hasChildren = metric.children && metric.children.length > 0;
            const isExpanded = expandedKeys.has(metric.key);

            const bgColor =
                depth === 0
                    ? '#fafafa'
                    : depth === 1
                    ? '#fff'
                    : '#f9f9f9';

            rows.push(
                <tr key={metric.key} style={{ backgroundColor: bgColor, color: metric.drillType !== 'none' ? '#1677ff' : undefined }}>
                    <td
                        style={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            backgroundColor: bgColor,
                            padding: '8px 12px',
                            borderBottom: '1px solid #f0f0f0',
                            whiteSpace: 'nowrap',
                            minWidth: 260,
                            maxWidth: 400,
                        }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, paddingLeft: depth * 16 }}>
                            {hasChildren && (
                                <span
                                    style={{ cursor: 'pointer', userSelect: 'none', fontSize: 12, color: '#666' }}
                                    onClick={() => onToggleExpand(metric.key)}
                                >
                                    {isExpanded ? '▼' : '▶'}
                                </span>
                            )}
                            {!hasChildren && <span style={{ display: 'inline-block', width: 16 }} />}
                            <span style={metric.drillType !== 'none' ? { color: '#1677ff' } : undefined}>{t(metric.label)}</span>
                            {metric.tooltip && (
                                <Tooltip title={metric.tooltip} placement="right">
                                    <InfoCircleOutlined style={{ fontSize: 12, color: '#8c8c8c', cursor: 'default' }} />
                                </Tooltip>
                            )}
                        </span>
                    </td>
                    <td
                        style={{
                            padding: '8px 12px',
                            borderBottom: '1px solid #f0f0f0',
                            borderRight: '1px solid #d9d9d9',
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                            minWidth: 100,
                            fontWeight: 600,
                            backgroundColor: bgColor,
                        }}
                    >
                        {renderMetricCell(metric, null, false)}
                    </td>
                    {periodKeys.map((pk) => (
                        <td
                            key={pk}
                            style={{
                                padding: '8px 12px',
                                borderBottom: '1px solid #f0f0f0',
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                                minWidth: 100,
                            }}
                        >
                            {renderMetricCell(metric, pk, false)}
                        </td>
                    ))}
                </tr>
            );

            if (hasChildren && isExpanded) {
                rows.push(...renderRows(metric.children!, depth + 1, metric.drillType !== 'none' ? metric.drillType : categoryDrillType));
            }
        });

        return rows;
    };

    const renderCategoryRows = (category: MetricCategory): React.ReactNode[] => {
        const rows: React.ReactNode[] = [];

        rows.push(
            <tr key={`cat-${category.key}`} style={{ backgroundColor: '#f3f0ff' }}>
                <td
                    style={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: '#f3f0ff',
                        padding: '8px 12px',
                        borderBottom: '1px solid #e8e8e8',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        minWidth: 260,
                    }}
                >
                    {t(category.label)}
                </td>
                <td
                    style={{
                        padding: '8px 12px',
                        borderBottom: '1px solid #e8e8e8',
                        borderRight: '1px solid #d9d9d9',
                    }}
                />
                {periodKeys.map((pk) => (
                    <td
                        key={pk}
                        style={{
                            padding: '8px 12px',
                            borderBottom: '1px solid #e8e8e8',
                        }}
                    />
                ))}
            </tr>
        );

        rows.push(...renderRows(category.children, 0, category.drillType));
        return rows;
    };

    return (
        <>
            {/* Table */}
            <Spin spinning={loading}>
                <div style={{ overflowX: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                    <table
                        style={{
                            borderCollapse: 'collapse',
                            width: '100%',
                            fontSize: 13,
                        }}
                    >
                        <thead>
                            <tr style={{ backgroundColor: '#1e2547' }}>
                                <th
                                    style={{
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 3,
                                        backgroundColor: '#1e2547',
                                        padding: '10px 12px',
                                        borderBottom: '2px solid #1e2547',
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        minWidth: 260,
                                        color: '#fff',
                                    }}
                                >
                                    指标
                                </th>
                                <th
                                    style={{
                                        padding: '10px 12px',
                                        borderBottom: '2px solid #1e2547',
                                        borderRight: '1px solid #4a5280',
                                        textAlign: 'right',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        minWidth: 100,
                                        color: '#fff',
                                    }}
                                >
                                    汇总
                                </th>
                                {periodKeys.map((pk) => (
                                    <th
                                        key={pk}
                                        style={{
                                            padding: '10px 12px',
                                            borderBottom: '2px solid #1e2547',
                                            textAlign: 'right',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            minWidth: 100,
                                            color: '#fff',
                                        }}
                                    >
                                        {formatPeriodHeader(pk, timeRange?.queryType)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleCategories.map((cat) => renderCategoryRows(cat))}
                        </tbody>
                    </table>
                </div>
            </Spin>

            {/* Drawers */}
            <PickupReservationDrawer
                visible={drillState.visible && drillState.type === 'reservation'}
                onClose={handleDrawerClose}
                startDate={timeRange?.startDate ?? ''}
                endDate={timeRange?.endDate ?? ''}
                hubIds={hubIdsForQuery}
                customerIds={customerIdsForQuery}
                ewrType={ewrType}
                bizDate={drillState.bizDate}
                drillFilter={drillState.drillFilter}
            />
            <PickupSiteDrawer
                visible={drillState.visible && drillState.type === 'site'}
                onClose={handleDrawerClose}
                startDate={timeRange?.startDate ?? ''}
                endDate={timeRange?.endDate ?? ''}
                hubIds={hubIdsForQuery}
                customerIds={customerIdsForQuery}
                ewrType={ewrType}
                bizDate={drillState.bizDate}
                drillFilter={drillState.drillFilter}
            />
        </>
    );
};

// ----------------------------- page wrapper (shared filters + query + timer) -----------------------------

const PICKUP_TAB_KEYS = ['overallResult', 'confirmSection', 'wiringSection', 'visitSection'];
const PACKAGE_ONLINE_TAB_KEYS = ['packageOnline'];
const NEW_SITE_TAB_KEYS = ['newSiteConnect'];

// 每个 tab 对应的可展开行 key 列表（模块加载时计算一次）
const EXPANDABLE_KEYS_BY_TAB: Record<string, string[]> = {
    pickup: getAllExpandableKeys(PICKUP_TAB_KEYS),
    packageOnline: getAllExpandableKeys(PACKAGE_ONLINE_TAB_KEYS),
    newSiteConnect: getAllExpandableKeys(NEW_SITE_TAB_KEYS),
};

const PickupDashboardPage: React.FC = () => {
    const { t } = useTranslation();
    const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
    const [selectedHubIds, setSelectedHubIds] = useState<number[]>([]);
    const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([]);
    const [selectedEwrType, setSelectedEwrType] = useState<string | undefined>(undefined);
    const [activeTabKey, setActiveTabKey] = useState<string>('pickup');
    // 各 tab 独立的展开状态
    const [expandedKeysByTab, setExpandedKeysByTab] = useState<Record<string, Set<string>>>({
        pickup: new Set<string>(),
        packageOnline: new Set<string>(),
        newSiteConnect: new Set<string>(),
    });

    const totalExpandableCount = Object.values(EXPANDABLE_KEYS_BY_TAB).reduce((s, keys) => s + keys.length, 0);
    const totalExpandedCount = Object.values(expandedKeysByTab).reduce((s, set) => s + set.size, 0);
    const allExpanded = totalExpandableCount > 0 && totalExpandedCount === totalExpandableCount;

    const handleExpandAll = () => {
        setExpandedKeysByTab({
            pickup: new Set(EXPANDABLE_KEYS_BY_TAB.pickup),
            packageOnline: new Set(EXPANDABLE_KEYS_BY_TAB.packageOnline),
            newSiteConnect: new Set(EXPANDABLE_KEYS_BY_TAB.newSiteConnect),
        });
    };

    const handleCollapseAll = () => {
        setExpandedKeysByTab({
            pickup: new Set<string>(),
            packageOnline: new Set<string>(),
            newSiteConnect: new Set<string>(),
        });
    };

    const handleToggleExpand = useCallback((tabKey: string) => (rowKey: string) => {
        setExpandedKeysByTab((prev) => {
            const current = prev[tabKey];
            const next = new Set(current);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return { ...prev, [tabKey]: next };
        });
    }, []);

    const handleTabChange = useCallback((key: string) => {
        if (activeTabKey === 'packageOnline' && key !== 'packageOnline' && selectedCustomerIds.length > 0) {
            setSelectedCustomerIds([]);
        }
        setActiveTabKey(key);
    }, [activeTabKey, selectedCustomerIds]);

    const { hubOptions, loading: hubsLoading } = useOperableHubSelectOptions({
        excludeLabels: [...DASHBOARD_EXCLUDE_HUB_LABELS],
        showAllOption: true,
        allOptionLabel: t('dashboard.pickup.allHub') as string,
    });

    const ewrHubId = useMemo(
        () => hubOptions.find((opt) => opt.label === 'EWR HUB')?.value ?? null,
        [hubOptions]
    );

    const isEwrHubSelected = useMemo(
        () => ewrHubId !== null && selectedHubIds.includes(ewrHubId),
        [selectedHubIds, ewrHubId]
    );

    const { data: customerOptionsData } = useQuery<QueryPickupCustomerOptionsResponse>(
        QUERY_PICKUP_CUSTOMER_OPTIONS
    );
    // 客户筛选项暂时隐藏，_customerOptions 保留供后续启用
    const _customerOptions = useMemo(() => {
        const opts = [{ value: 0, label: t('dashboard.pickup.allCustomer') }];
        (customerOptionsData?.queryPickupCustomerOptions ?? []).forEach((c) => {
            opts.push({ value: c.id, label: c.name });
        });
        return opts;
    }, [customerOptionsData, t]);

    const hubIdsForQuery = useMemo(() => {
        if (selectedHubIds.length === 0 || selectedHubIds.includes(0)) return undefined;
        return selectedHubIds;
    }, [selectedHubIds]);

    const customerIdsForQuery = useMemo(() => {
        if (selectedCustomerIds.length === 0 || selectedCustomerIds.includes(0)) return undefined;
        return selectedCustomerIds;
    }, [selectedCustomerIds]);

    const ewrTypeForQuery = isEwrHubSelected ? selectedEwrType : undefined;

    const { data, loading, refetch } = useQuery<QueryPickupDashboardResponse>(QUERY_PICKUP_DASHBOARD, {
        variables: {
            input: {
                startDate: timeRange?.startDate,
                endDate: timeRange?.endDate,
                hubIds: hubIdsForQuery,
                customerIds: customerIdsForQuery,
                ewrType: ewrTypeForQuery,
                queryType: timeRange?.queryType,
            },
        },
        skip: !timeRange,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    // 自动刷新：OFF | 300000(5min) | 900000(15min) | 3600000(1h)
    const [refreshInterval, setRefreshInterval] = useState<number | 'OFF'>('OFF');
    const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
        if (refreshInterval !== 'OFF' && timeRange) {
            refreshTimerRef.current = setInterval(() => { void refetch(); }, refreshInterval);
        }
        return () => { if (refreshTimerRef.current) clearInterval(refreshTimerRef.current); };
    }, [refreshInterval, timeRange, refetch]);

    const items = data?.queryPickupDashboard?.items ?? [];
    const latestCalculatedAt = data?.queryPickupDashboard?.latestCalculatedAt ?? null;

    const itemsByPeriod = useMemo(() => {
        const qt = timeRange?.queryType ?? ScaleQueryType.DAILY;
        const map = new Map<string, PickupDashboardSummaryItem[]>();
        items.forEach((item) => {
            const key = computePeriodKey(item.bizDate, qt);
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(item);
        });
        return map;
    }, [items, timeRange]);

    const periodKeys = useMemo(() => {
        if (!timeRange) return [];
        const { startDate, endDate, queryType } = timeRange;
        const qt = queryType ?? ScaleQueryType.DAILY;
        const keys: string[] = [];

        if (qt === ScaleQueryType.DAILY) {
            let cur = dayjs(startDate);
            const endDay = dayjs(endDate);
            while (!cur.isAfter(endDay)) {
                keys.push(cur.format('YYYY-MM-DD'));
                cur = cur.add(1, 'day');
            }
        } else if (qt === ScaleQueryType.WEEKLY) {
            let cur = dayjs(startDate).startOf('isoWeek');
            const endDay = dayjs(endDate);
            while (!cur.isAfter(endDay)) {
                keys.push(`${cur.format('YYYY-MM-DD')}~${cur.endOf('isoWeek').format('YYYY-MM-DD')}`);
                cur = cur.add(1, 'week');
            }
        } else {
            let cur = dayjs(startDate).startOf('month');
            const endDay = dayjs(endDate);
            while (!cur.isAfter(endDay)) {
                keys.push(cur.format('YYYY-MM'));
                cur = cur.add(1, 'month');
            }
        }
        return keys;
    }, [timeRange]);

    const sharedTabProps = {
        items,
        itemsByPeriod,
        periodKeys,
        loading,
        timeRange,
        hubIdsForQuery,
        customerIdsForQuery,
        ewrType: ewrTypeForQuery,
    };

    return (
        <div className="pickup-dashboard-page" style={{ padding: 16 }}>
            <style>{`
                .pickup-dashboard-page .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
                    background: #7c3aed !important;
                    border-color: #7c3aed !important;
                    color: #fff !important;
                }
                .pickup-dashboard-page .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before {
                    background-color: #7c3aed !important;
                }
                .pickup-dashboard-page .ant-radio-button-wrapper:hover {
                    color: #7c3aed !important;
                }
                .pickup-dashboard-page .ant-radio-button-wrapper {
                    font-weight: 600;
                }
            `}</style>

            {/* Dashboard description */}
            <div style={{ marginBottom: 8 }}>
                <span className="text-xl font-semibold">
                    揽收数据看板
                    <span className="text-sm font-normal ml-3" style={{ color: '#595959' }}>
                        （时区说明: 日期为HUB所在时区{latestCalculatedAt ? `；数据更新于: ${dayjs(latestCalculatedAt).tz(DASHBOARD_PACIFIC_TZ).format('YYYY-MM-DD HH:mm')} PST` : ''}）
                    </span>
                </span>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <TimeRangeFilter
                    onChange={setTimeRange}
                    dashboardTimezone="America/Los_Angeles"
                    presetKeysByQueryType={{
                        [ScaleQueryType.DAILY]: ['last7days', 'last14days', 'last30days', 'custom'],
                        [ScaleQueryType.WEEKLY]: ['last4weeks', 'last8weeks', 'last12weeks', 'custom'],
                        [ScaleQueryType.MONTHLY]: ['last3months', 'last6months', 'last12months', 'custom'],
                    }}
                    defaultPresetByQueryType={{
                        [ScaleQueryType.DAILY]: 'last14days',
                        [ScaleQueryType.WEEKLY]: 'last8weeks',
                        [ScaleQueryType.MONTHLY]: 'last6months',
                    }}
                />
                <Select
                    mode="multiple"
                    placeholder={t('dashboard.pickup.allHub')}
                    options={hubOptions}
                    value={selectedHubIds}
                    onChange={(values: number[]) => {
                        const prev = selectedHubIds;
                        const hadAll = prev.includes(0);
                        const nowHasAll = values.includes(0);
                        let next: number[];
                        if (!hadAll && nowHasAll) {
                            next = [0];
                        } else if (hadAll && values.length > 1) {
                            next = values.filter(v => v !== 0);
                        } else {
                            next = values;
                        }
                        setSelectedHubIds(next);
                        if (ewrHubId !== null && !next.includes(ewrHubId)) {
                            setSelectedEwrType(undefined);
                        }
                    }}
                    loading={hubsLoading}
                    allowClear
                    style={{ minWidth: 180 }}
                    maxTagCount="responsive"
                />
                {isEwrHubSelected && (
                    <Select
                        placeholder="NJ/JFK"
                        options={[{ value: 'NJ', label: 'NJ' }, { value: 'JFK', label: 'JFK' }, { value: 'OTHER', label: 'OTHER' }]}
                        value={selectedEwrType}
                        onChange={(v: string) => setSelectedEwrType(v)}
                        allowClear
                        onClear={() => setSelectedEwrType(undefined)}
                        style={{ minWidth: 100 }}
                    />
                )}
                {activeTabKey === 'packageOnline' && (
                    <Select
                        mode="multiple"
                        placeholder={t('dashboard.pickup.allCustomer')}
                        options={_customerOptions}
                        value={selectedCustomerIds}
                        onChange={(values: number[]) => {
                            const hadAll = selectedCustomerIds.includes(0);
                            const nowHasAll = values.includes(0);
                            if (!hadAll && nowHasAll) {
                                setSelectedCustomerIds([0]);
                            } else if (hadAll && values.length > 1) {
                                setSelectedCustomerIds(values.filter(v => v !== 0));
                            } else {
                                setSelectedCustomerIds(values);
                            }
                        }}
                        allowClear
                        style={{ minWidth: 180 }}
                        maxTagCount="responsive"
                    />
                )}
                {/* 自动刷新：刷新图标 + 时间间隔选择 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                    <Tooltip title="立即刷新">
                        <Button
                            icon={<ReloadOutlined spin={loading} />}
                            size="small"
                            onClick={() => { void refetch(); }}
                        />
                    </Tooltip>
                    <Select
                        size="small"
                        value={refreshInterval}
                        onChange={(v) => setRefreshInterval(v as number | 'OFF')}
                        style={{ width: 110 }}
                        options={[
                            { value: 'OFF', label: 'OFF' },
                            { value: 300000,  label: '5 mins' },
                            { value: 900000,  label: '15 mins' },
                            { value: 3600000, label: '1 hour' },
                        ]}
                    />
                </div>
            </div>

            {/* Color legend + expand/collapse */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 4, fontSize: 13 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, border: '1.5px solid #52c41a', backgroundColor: 'transparent' }} />
                    <span>≥95%</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, border: '1.5px solid #faad14', backgroundColor: 'transparent' }} />
                    <span>85%—95%</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, border: '1.5px solid #ff4d4f', backgroundColor: 'transparent' }} />
                    <span>&lt;85%</span>
                </span>
                <Button size="small" style={{ marginLeft: 'auto' }} onClick={allExpanded ? handleCollapseAll : handleExpandAll}>
                    {allExpanded ? t('dashboard.pickup.collapseAll') : t('dashboard.pickup.expandAll')}
                </Button>
            </div>

            {/* Tabs */}
            <Tabs
                activeKey={activeTabKey}
                onChange={handleTabChange}
                items={[
                    {
                        key: 'pickup',
                        label: '揽收执行',
                        children: (
                            <PickupTabContent
                                {...sharedTabProps}
                                allowedCategoryKeys={PICKUP_TAB_KEYS}
                                expandedKeys={expandedKeysByTab.pickup}
                                onToggleExpand={handleToggleExpand('pickup')}
                            />
                        ),
                    },
                    {
                        key: 'newSiteConnect',
                        label: '新仓建联',
                        children: (
                            <PickupTabContent
                                {...sharedTabProps}
                                allowedCategoryKeys={NEW_SITE_TAB_KEYS}
                                expandedKeys={expandedKeysByTab.newSiteConnect}
                                onToggleExpand={handleToggleExpand('newSiteConnect')}
                            />
                        ),
                    },
                    {
                        key: 'packageOnline',
                        label: '包裹上网',
                        children: (
                            <PickupTabContent
                                {...sharedTabProps}
                                allowedCategoryKeys={PACKAGE_ONLINE_TAB_KEYS}
                                expandedKeys={expandedKeysByTab.packageOnline}
                                onToggleExpand={handleToggleExpand('packageOnline')}
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
};

export default PickupDashboardPage;
